import { Package, Truck } from 'lucide-react';
import { type ShippingOrderInfo } from '../../types/ai';

type PlaygroundShippingPanelProps = {
  latestShippingInfo: ShippingOrderInfo;
};

export const PlaygroundShippingPanel = ({ latestShippingInfo }: PlaygroundShippingPanelProps) => (
  <div className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      <Package className="h-5 w-5 text-brand" />
      <h3 className="text-base font-semibold text-brand-blue">Order & Frakt</h3>
      {latestShippingInfo.isVerified === false && (
        <span className="ml-auto px-2 py-1 rounded text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/40">
          🔒 Begränsad vy
        </span>
      )}
      {latestShippingInfo.isVerified === true && (
        <span className="ml-auto px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/40">
          ✓ Verifierad
        </span>
      )}
    </div>

    {latestShippingInfo.isVerified === false && (
      <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
        ⚠️ Viss information är dold av säkerhetsskäl. Ange postnummer för att se fullständig
        orderinformation.
      </div>
    )}

    {latestShippingInfo.hasReturn && (
      <div className="mb-3 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
        ↩️ Denna order har en retur
        {latestShippingInfo.returnStatus && ` - Status: ${latestShippingInfo.returnStatus}`}
      </div>
    )}

    <div className="space-y-3 text-sm">
      <div className="rounded-md border border-slate-800/80 bg-slate-950/40 p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Order {latestShippingInfo.orderNumber}
          </span>
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              latestShippingInfo.status === 'delivered'
                ? 'bg-green-500/20 text-green-400'
                : latestShippingInfo.status === 'shipped'
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'bg-yellow-500/20 text-yellow-400'
            }`}
          >
            {latestShippingInfo.status === 'delivered'
              ? 'Levererad'
              : latestShippingInfo.status === 'shipped'
                ? 'Skickad'
                : latestShippingInfo.status === 'processed'
                  ? 'Behandlad'
                  : latestShippingInfo.status === 'processing'
                    ? 'Behandlas'
                    : latestShippingInfo.status}
          </span>
        </div>

        {latestShippingInfo.estimatedDelivery && (
          <p className="text-slate-300">
            <span className="text-slate-500">Beräknad leverans:</span>{' '}
            {latestShippingInfo.estimatedDelivery}
          </p>
        )}
      </div>

      {latestShippingInfo.hasShipping ? (
        <div className="rounded-md border border-slate-800/80 bg-slate-950/40 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="h-4 w-4 text-brand" />
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Frakt
            </span>
          </div>

          {latestShippingInfo.carrier && (
            <p className="text-slate-300">
              <span className="text-slate-500">Fraktbolag:</span> {latestShippingInfo.carrier}
            </p>
          )}

          {latestShippingInfo.trackingNumber && (
            <p
              className={`text-slate-300 ${latestShippingInfo.isVerified === false ? 'blur-sm select-none' : ''}`}
            >
              <span className="text-slate-500">Spårningsnummer:</span>{' '}
              {latestShippingInfo.trackingNumber}
            </p>
          )}

          {latestShippingInfo.trackingUrl && (
            <a
              href={latestShippingInfo.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs font-semibold text-brand hover:text-brand-light"
            >
              Spåra paket →
            </a>
          )}

          {latestShippingInfo.trackingDetails?.events &&
            latestShippingInfo.trackingDetails.events.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-800">
                {latestShippingInfo.trackingDetails.events.some((e) => !e.isReturn) && (
                  <>
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 block mb-2">
                      📦 Spårningshistorik (leverans)
                    </span>
                    <div className="space-y-2 max-h-32 overflow-y-auto mb-3">
                      {latestShippingInfo.trackingDetails.events
                        .filter((e) => !e.isReturn)
                        .slice(0, 3)
                        .map((event, index) => (
                          <div key={index} className="text-xs">
                            <p className="text-slate-300">
                              {new Date(event.timestamp).toLocaleDateString('sv-SE', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                            <p className="text-slate-400 text-xs">
                              {event.description}
                              {event.location && ` - ${event.location}`}
                            </p>
                          </div>
                        ))}
                    </div>
                  </>
                )}

                {latestShippingInfo.trackingDetails.events.some((e) => e.isReturn) && (
                  <>
                    <span className="text-xs font-semibold uppercase tracking-wide text-rose-400 block mb-2">
                      ↩️ Spårningshistorik (retur)
                    </span>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {latestShippingInfo.trackingDetails.events
                        .filter((e) => e.isReturn)
                        .slice(0, 3)
                        .map((event, index) => (
                          <div key={index} className="text-xs">
                            <p className="text-slate-300">
                              {new Date(event.timestamp).toLocaleDateString('sv-SE', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                            <p className="text-slate-400 text-xs">
                              {event.description}
                              {event.location && ` - ${event.location}`}
                              {event.returnReason && ` (Orsak: ${event.returnReason})`}
                            </p>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            )}

          {latestShippingInfo.fees && latestShippingInfo.fees.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-800">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 block mb-2">
                Fraktavgifter
              </span>
              <div className="space-y-1">
                {latestShippingInfo.fees.map((fee) => (
                  <p key={fee.id} className="text-xs text-slate-400">
                    {fee.name}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
          <p className="text-xs text-amber-200">
            Denna order använder en leveransmetod som inte kan spåras automatiskt.
          </p>
        </div>
      )}

      {latestShippingInfo.shippingAddress && (
        <div className="rounded-md border border-slate-800/80 bg-slate-950/40 p-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 block mb-2">
            Leveransadress
          </span>
          <div
            className={`text-xs text-slate-300 ${latestShippingInfo.isVerified === false ? 'blur-sm select-none' : ''}`}
          >
            <p>
              {latestShippingInfo.shippingAddress.firstName}{' '}
              {latestShippingInfo.shippingAddress.lastName}
            </p>
            <p>{latestShippingInfo.shippingAddress.streetAddress}</p>
            <p>
              {latestShippingInfo.shippingAddress.postalCode}{' '}
              {latestShippingInfo.shippingAddress.city}
            </p>
            <p>{latestShippingInfo.shippingAddress.country}</p>
          </div>
        </div>
      )}
    </div>
  </div>
);
